import {
  Button,
  DatePicker,
  Divider,
  Form,
  Input,
  InputNumber,
  Select,
  Space,
  message,
  Radio,
} from "antd";
import dayjs from "dayjs";
import React, { useState } from "react";
import styled from "styled-components";
import JsonEditor from "./JsonEditor";
import {
  AWSAISettings,
  CreateTenantDTO,
  DeepseekAISettings,
  GenbiAISettings,
  ModelProvider,
  OpenaiAISettings,
} from "../service";

const StyledForm = styled(Form<CreateTenantFormValues>)`
  .ant-form-item-label {
    font-weight: 500;
  }
`;

const StyledDivider = styled(Divider)`
  &.ant-divider-with-text {
    margin: 24px 0 16px;
  }
`;

const StyledAlert = styled.div`
  color: #ff4d4f;
  font-size: 12px;
  margin-top: -16px;
  margin-bottom: 16px;
`;

const defaultAwsSettings: AWSAISettings = {
  aws_access_key: "",
  aws_secret_key: "",
  aws_assume_role_arn: "",
  aws_region: "",
  models: [],
};

const defaultOpenaiSettings: OpenaiAISettings = {
  openai_api_key: "",
  openai_project: "",
  models: [],
};

const defaultGenbiSettings: GenbiAISettings = {
  openai_api_key: "",
  openai_project: "",
  models: [],
};

const defaultDeepseekSettings: DeepseekAISettings = {
  deepseek_api_key: "",
  models: [],
};

export interface CreateTenantFormValues
  extends Omit<
    CreateTenantDTO,
    | "valid_until"
    | "aws_settings"
    | "openai_settings"
    | "genbi_settings"
    | "deepseek_settings"
  > {
  valid_until: dayjs.Dayjs;
  aws_settings: string;
  openai_settings: string;
  genbi_settings: string;
  deepseek_settings: string;
}

interface CreateTenantFormProps {
  onSubmit: (values: CreateTenantDTO) => Promise<void>;
  onCancel: () => void;
  onReset: () => void;
  submitting: boolean;
}

interface ParsedSettings {
  awsSettings?: Omit<AWSAISettings, "provider">;
  openaiSettings?: Omit<OpenaiAISettings, "provider">;
  genbiSettings?: Omit<GenbiAISettings, "provider">;
  deepseekSettings?: Omit<DeepseekAISettings, "provider">;
}

const CreateTenantForm: React.FC<CreateTenantFormProps> = ({
  onSubmit,
  onCancel,
  onReset,
  submitting,
}) => {
  const [form] = Form.useForm<CreateTenantFormValues>();
  const [selectedProvider, setSelectedProvider] =
    useState<ModelProvider>("genbi");
  const [awsSettingsError, setAwsSettingsError] = useState<string>();
  const [openaiSettingsError, setOpenaiSettingsError] = useState<string>();
  const [deepseekSettingsError, setDeepseekSettingsError] = useState<string>();
  const [genbiSettingsError, setGenbiSettingsError] = useState<string>();

  const parseSettings = (jsonString: string): any => {
    if (!jsonString.trim()) {
      return undefined;
    }
    try {
      const parsed = JSON.parse(jsonString);
      // Remove empty string values
      Object.keys(parsed).forEach((key) => {
        if (parsed[key] === "") {
          delete parsed[key];
        }
      });
      // If all values are empty, return undefined
      return Object.keys(parsed).length > 0 ? parsed : undefined;
    } catch (error) {
      throw new Error("Invalid JSON format");
    }
  };

  const validateAISettings = async (): Promise<ParsedSettings> => {
    try {
      const awsSettingsStr = form.getFieldValue("aws_settings");
      const openaiSettingsStr = form.getFieldValue("openai_settings");
      const deepseekSettingsStr = form.getFieldValue("deepseek_settings");
      const genbiSettingsStr = form.getFieldValue("genbi_settings");

      const awsSettings = parseSettings(awsSettingsStr);
      const openaiSettings = parseSettings(openaiSettingsStr);
      const deepseekSettings = parseSettings(deepseekSettingsStr);
      const genbiSettings = parseSettings(genbiSettingsStr);

      // Only validate the selected provider's settings
      if (selectedProvider === "aws") {
        if (!awsSettings?.aws_access_key || !awsSettings?.aws_secret_key) {
          throw new Error(
            "AWS settings require both access key and secret key",
          );
        }
        if (!awsSettings?.aws_region) {
          throw new Error("AWS settings require a region");
        }
        if (!awsSettings?.aws_assume_role_arn) {
          throw new Error("AWS settings require an assume role ARN");
        }
        if (!awsSettings?.models || awsSettings.models.length === 0) {
          throw new Error("AWS settings require at least one model");
        }
      } else if (selectedProvider === "openai") {
        if (!openaiSettings?.openai_api_key) {
          throw new Error("OpenAI settings require an API key");
        }
        if (!openaiSettings?.openai_project) {
          throw new Error("OpenAI settings require a project ID");
        }
        if (!openaiSettings?.models || openaiSettings.models.length === 0) {
          throw new Error("OpenAI settings require at least one model");
        }
      } else if (selectedProvider === "deepseek") {
        if (!deepseekSettings?.deepseek_api_key) {
          throw new Error("DeepSeek settings require an API key");
        }
        if (!deepseekSettings?.models || deepseekSettings.models.length === 0) {
          throw new Error("DeepSeek settings require at least one model");
        }
      } else if (selectedProvider === "genbi") {
        if (!genbiSettings?.openai_api_key) {
          throw new Error("GenBI settings require an API key");
        }
        if (!genbiSettings?.openai_project) {
          throw new Error("GenBI settings require a project ID");
        }
        if (!genbiSettings?.models || genbiSettings.models.length === 0) {
          throw new Error("GenBI settings require at least one model");
        }
      }

      return {
        awsSettings:
          selectedProvider === "aws" ? { ...awsSettings } : undefined,
        openaiSettings:
          selectedProvider === "openai" ? { ...openaiSettings } : undefined,
        genbiSettings:
          selectedProvider === "genbi" ? { ...genbiSettings } : undefined,
        deepseekSettings:
          selectedProvider === "deepseek" ? { ...deepseekSettings } : undefined,
      };
    } catch (error) {
      if (error instanceof Error) {
        throw new Error(error.message);
      }
      throw error;
    }
  };

  const handleSubmit = async (values: CreateTenantFormValues) => {
    try {
      const { awsSettings, openaiSettings, genbiSettings, deepseekSettings } =
        await validateAISettings();

      // Convert form values to DTO
      const formData = {
        tenant_name: values.tenant_name,
        tenant_code: values.tenant_code,
        max_users: values.max_users,
        valid_until: values.valid_until.toDate(),
        model_provider: values.model_provider,
        aws_settings: awsSettings,
        openai_settings: openaiSettings,
        genbi_settings: genbiSettings,
        deepseek_settings: deepseekSettings,
        status: values.status,
      } as CreateTenantDTO;

      await onSubmit(formData as any); // Type assertion needed due to valid_until type mismatch
    } catch (error) {
      if (error instanceof Error) {
        if (error.message === "Invalid JSON format") {
          message.error("Please check the JSON format of your settings");
        } else {
          message.error(error.message);
        }
      }
    }
  };

  const handleAwsSettingsChange = (
    e: React.ChangeEvent<HTMLTextAreaElement>,
  ) => {
    try {
      const value = e.target.value;
      if (value.trim()) {
        JSON.parse(value);
        setAwsSettingsError(undefined);
      }
    } catch (error) {
      setAwsSettingsError("Invalid JSON format");
    }
  };

  const handleOpenaiSettingsChange = (
    e: React.ChangeEvent<HTMLTextAreaElement>,
  ) => {
    try {
      const value = e.target.value;
      if (value.trim()) {
        JSON.parse(value);
        setOpenaiSettingsError(undefined);
      }
    } catch (error) {
      setOpenaiSettingsError("Invalid JSON format");
    }
  };

  const handleGenbiSettingsChange = (
    e: React.ChangeEvent<HTMLTextAreaElement>,
  ) => {
    try {
      const value = e.target.value;
      if (value.trim()) {
        JSON.parse(value);
        setGenbiSettingsError(undefined);
      }
    } catch (error) {
      setGenbiSettingsError("Invalid JSON format");
    }
  };

  const handleProviderChange = (value: ModelProvider) => {
    setSelectedProvider(value);
    form.setFieldValue("model_provider", value);
    // Clear errors for other providers
    setAwsSettingsError(undefined);
    setOpenaiSettingsError(undefined);
    setDeepseekSettingsError(undefined);
    setGenbiSettingsError(undefined);
  };

  const handleDeepseekSettingsChange = (
    e: React.ChangeEvent<HTMLTextAreaElement>,
  ) => {
    try {
      const value = e.target.value;
      if (value.trim()) {
        JSON.parse(value);
        setDeepseekSettingsError(undefined);
      }
    } catch (error) {
      setDeepseekSettingsError("Invalid JSON format");
    }
  };

  return (
    <StyledForm
      form={form}
      layout="vertical"
      onFinish={handleSubmit}
      initialValues={{
        model_provider: "genbi",
        max_users: 10,
        aws_settings: JSON.stringify(defaultAwsSettings, null, 2),
        openai_settings: JSON.stringify(defaultOpenaiSettings, null, 2),
        genbi_settings: JSON.stringify({}, null, 2),
        deepseek_settings: JSON.stringify({}, null, 2),
      }}
    >
      <Form.Item
        label="Tenant Name"
        name="tenant_name"
        rules={[{ required: true, message: "Please input tenant name!" }]}
      >
        <Input placeholder="Enter tenant name" />
      </Form.Item>

      <Form.Item
        label="Tenant Code"
        name="tenant_code"
        rules={[{ required: true, message: "Please input tenant code!" }]}
      >
        <Input placeholder="Enter tenant code (e.g., tenant-123)" />
      </Form.Item>

      <Form.Item
        label="Max Users"
        name="max_users"
        rules={[
          { required: true, message: "Please input max users!" },
          { type: "number", min: 1, message: "Must be at least 1" },
        ]}
      >
        <InputNumber style={{ width: "100%" }} />
      </Form.Item>

      <Form.Item
        label="Valid Until"
        name="valid_until"
        rules={[{ required: true, message: "Please select valid until date!" }]}
      >
        <DatePicker
          style={{ width: "100%" }}
          disabledDate={(current) =>
            current && current < dayjs().startOf("day")
          }
        />
      </Form.Item>

      <Form.Item
        label="Model Provider"
        name="model_provider"
        rules={[{ required: true, message: "Please select a model provider!" }]}
        tooltip="Choose the AI model provider for this tenant."
      >
        <Select
          placeholder="Select model provider"
          onChange={handleProviderChange}
          options={[
            { value: "genbi", label: "GenBI" },
            { value: "openai", label: "OpenAI" },
            { value: "aws", label: "AWS" },
            { value: "deepseek", label: "DeepSeek" },
          ]}
        />
      </Form.Item>

      <>
        <StyledDivider orientation="left">AI Settings</StyledDivider>
        <StyledAlert>
          Please configure the settings for the selected provider
        </StyledAlert>

        {selectedProvider === "aws" && (
          <Form.Item
            label="AWS Settings"
            name="aws_settings"
            validateStatus={awsSettingsError ? "error" : undefined}
            help={awsSettingsError}
            tooltip="Enter AWS settings in JSON format. Required fields: aws_access_key, aws_secret_key"
          >
            <JsonEditor
              value={form.getFieldValue("aws_settings")}
              onChange={(value) => {
                form.setFieldValue("aws_settings", value || "");
                handleAwsSettingsChange({
                  target: { value: value || "" },
                } as React.ChangeEvent<HTMLTextAreaElement>);
              }}
              placeholder={JSON.stringify(defaultAwsSettings, null, 2)}
              height={150}
              showError={true}
              errorMessage={awsSettingsError}
            />
          </Form.Item>
        )}

        {selectedProvider === "openai" && (
          <Form.Item
            label="OpenAI Settings"
            name="openai_settings"
            validateStatus={openaiSettingsError ? "error" : undefined}
            help={openaiSettingsError}
            tooltip="Enter OpenAI settings in JSON format. Required field: openai_api_key"
          >
            <JsonEditor
              value={form.getFieldValue("openai_settings")}
              onChange={(value) => {
                form.setFieldValue("openai_settings", value || "");
                handleOpenaiSettingsChange({
                  target: { value: value || "" },
                } as React.ChangeEvent<HTMLTextAreaElement>);
              }}
              placeholder={JSON.stringify(defaultOpenaiSettings, null, 2)}
              height={150}
              showError={true}
              errorMessage={openaiSettingsError}
            />
          </Form.Item>
        )}

        {selectedProvider === "deepseek" && (
          <Form.Item
            label="DeepSeek Settings"
            name="deepseek_settings"
            validateStatus={deepseekSettingsError ? "error" : undefined}
            help={deepseekSettingsError}
            tooltip="Enter DeepSeek settings in JSON format. Required field: deepseek_api_key"
          >
            <JsonEditor
              value={form.getFieldValue("deepseek_settings")}
              onChange={(value) => {
                form.setFieldValue("deepseek_settings", value || "");
                handleDeepseekSettingsChange({
                  target: { value: value || "" },
                } as React.ChangeEvent<HTMLTextAreaElement>);
              }}
              placeholder={JSON.stringify(defaultDeepseekSettings, null, 2)}
              height={150}
              showError={true}
              errorMessage={deepseekSettingsError}
            />
          </Form.Item>
        )}

        {selectedProvider === "genbi" && (
          <Form.Item
            label="GenBI AI Settings"
            name="genbi_settings"
            validateStatus={genbiSettingsError ? "error" : undefined}
            help={genbiSettingsError}
            tooltip="Enter GenBI AI settings in JSON format. Required field: genbi_api_key"
          >
            <JsonEditor
              value={form.getFieldValue("genbi_settings")}
              onChange={(value) => {
                form.setFieldValue("genbi_settings", value || "");
                handleGenbiSettingsChange({
                  target: { value: value || "" },
                } as React.ChangeEvent<HTMLTextAreaElement>);
              }}
              placeholder={JSON.stringify(defaultGenbiSettings, null, 2)}
              height={150}
              showError={true}
              errorMessage={genbiSettingsError}
            />
          </Form.Item>
        )}
      </>

      <Form.Item
        label="Status"
        name="status"
        rules={[{ required: true, message: "Please select status!" }]}
      >
        <Radio.Group>
          <Radio value="active">Active</Radio>
          <Radio value="inactive">Inactive</Radio>
        </Radio.Group>
      </Form.Item>

      <Form.Item style={{ marginTop: 24, marginBottom: 0, textAlign: "right" }}>
        <Space>
          <Button onClick={onReset}>Reset</Button>
          <Button onClick={onCancel}>Cancel</Button>
          <Button type="primary" htmlType="submit" loading={submitting}>
            Create
          </Button>
        </Space>
      </Form.Item>
    </StyledForm>
  );
};

export default CreateTenantForm;
